import { type BucketLogData, bucketLog } from '@bucketplace/bucket-log';
import {
  Box,
  Flex,
  FONT_STYLE,
  type HtmlButtonProps,
  MEDIA_QUERY,
  Text,
  useResponsive,
} from '@bucketplace/design-system';
import { IconPlayFilled, IconX } from '@bucketplace/icons';
import { getVar, useTheme } from '@bucketplace/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  useState,
} from 'react';

import { REBRANDING_THEME } from '@/brand/common/style';
import type { ContentsCardType } from '@/brand/types';
import { ModalButtonWrapper } from '../ModalButtonWrapper';
import { VideoPreview } from '../VideoPreview';

interface ContentsCardProps {
  item: ContentsCardType;
  logData: BucketLogData;
}

const BACKGROUND_GRADIENT =
  'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgb(17 17 17 / 10%) 20%, rgb(17 17 17 / 70%) 100%)';

export const ContentsCard = forwardRef<HTMLDivElement, ContentsCardProps>(
  ({ item, logData }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const { cssVar } = useTheme<typeof REBRANDING_THEME>();
    const { breakpoint } = useResponsive();
    const isMobile = breakpoint === 'mobile' || breakpoint === 'tablet';

    const renderContent = (): ReactElement | null => {
      switch (item.type) {
        case 'video':
          return (
            <>
              <VideoComponent src={item.previewUrl} />
              <AlignContainer align={item.titleAlign}>
                <Text
                  variant={{
                    mobile: 'heading18',
                    tablet: 'heading20',
                    laptop: 'heading24',
                    desktop: 'heading24',
                  }}
                  align="center"
                  color="foregroundInverse"
                  weight={600}
                >
                  {item.title}
                </Text>
                <ModalButtonWrapper
                  renderModal={({ isOpen, onClose }) =>
                    isOpen && (
                      <VideoPreview
                        type="video"
                        url={item.url}
                        autoPlay
                        title={item.title}
                        onClose={() => {
                          bucketLog({
                            ...logData,
                            category: 'click',
                            type: 'button',
                            id: 'CLOSE',
                            data: {
                              content_title: item.title,
                            },
                          });

                          onClose();
                        }}
                      />
                    )
                  }
                >
                  {({ onClick }) => (
                    <ButtonComponent
                      type="button"
                      onClick={() => {
                        bucketLog({
                          ...logData,
                          category: 'click',
                          type: 'button',
                          id: 'PLAY',
                          data: {
                            content_title: item.title,
                          },
                        });

                        onClick();
                      }}
                    >
                      <IconPlayFilled
                        weight="regular"
                        size={18}
                        aspect="width"
                        color="white"
                      />
                      영상 보기
                    </ButtonComponent>
                  )}
                </ModalButtonWrapper>
              </AlignContainer>
            </>
          );

        case 'backgroundVideo':
          return (
            <>
              <VideoComponent src={item.previewUrl} hasBlur />
              <BlurText>{item.title}</BlurText>
            </>
          );
        case 'image':
          return (
            <Image src={item.src} loading="lazy" alt="브랜드 콘텐츠 이미지" />
          );
        case 'contents':
          return (
            <Box w="100%" h="100%">
              <Image
                src={item.imageUrl}
                loading="lazy"
                alt=""
                aria-hidden="true"
              />
              <AlignContainer align={item.titleAlign}>
                <Text
                  variant={{
                    mobile: 'heading18',
                    tablet: 'heading20',
                    laptop: 'heading24',
                    desktop: 'heading24',
                  }}
                  color="foregroundInverse"
                  weight={600}
                  align="center"
                >
                  {item.title}
                </Text>
                <ButtonComponent
                  type="button"
                  onClick={() => {
                    bucketLog({
                      ...logData,
                      category: 'click',
                      type: 'button',
                      id: 'SEE_MORE',
                      data: {
                        content_title: item.title,
                      },
                    });
                    setIsOpen(true);
                  }}
                >
                  이야기 보기
                </ButtonComponent>
              </AlignContainer>
              <SlideBox
                isOpen={isOpen}
                role="dialog"
                aria-modal="true"
                aria-hidden={!isOpen}
                tabIndex={0}
              >
                <Text
                  weight={500}
                  color="foregroundInverse"
                  whiteSpace="pre-line"
                  css={{
                    fontSize: isMobile ? '15px' : '18px',
                    lineHeight: isMobile ? '24px' : '30px',
                  }}
                >
                  {item[isMobile ? 'mobileText' : 'pcText']}
                </Text>
                <Flex direction="row" justify="space-between" align="flex-end">
                  <Text
                    color="foregroundInverse"
                    variant={{
                      mobile: 'body14L20',
                      tablet: 'body14L20',
                      laptop: 'body16L24',
                      desktop: 'body16L24',
                    }}
                    weight={600}
                    css={{ opacity: 0.6 }}
                  >
                    {item.writer}
                  </Text>
                  <StyledCloseButton
                    type="button"
                    aria-label="닫기"
                    onClick={() => {
                      bucketLog({
                        ...logData,
                        category: 'click',
                        type: 'button',
                        id: 'CLOSE',
                        data: {
                          content_title: item.title,
                        },
                      });

                      setIsOpen(false);
                    }}
                  >
                    <IconX
                      size={isMobile ? 14 : 20}
                      aspect="width"
                      color="white"
                      weight="semibold"
                      aria-hidden="true"
                      focusable="false"
                    />
                  </StyledCloseButton>
                </Flex>
              </SlideBox>
            </Box>
          );

        default:
          return null;
      }
    };

    return (
      <Box
        position="relative"
        w={item.width}
        h={item.height}
        bdr={item.shape === 'circle' ? '50%' : '24px'}
        overflow="hidden"
        style={{
          backgroundColor: getVar(cssVar('contents-card-background')),
          isolation: 'isolate',
        }}
        ref={ref}
      >
        {renderContent()}
      </Box>
    );
  },
);

function VideoComponent({
  src,
  hasBlur,
}: {
  src: string;
  hasBlur?: boolean;
}): ReactElement {
  return (
    <>
      <Video src={src} autoPlay muted loop preload="metadata" playsInline />
      {hasBlur && <Blur />}
    </>
  );
}

function ButtonComponent({
  children,
  ...props
}: PropsWithChildren<HtmlButtonProps>): ReactElement {
  return <StyledButton {...props}>{children}</StyledButton>;
}

function AlignContainer({
  children,
  align = 'bottom',
}: PropsWithChildren<{ align?: 'center' | 'bottom' }>): ReactElement {
  return (
    <Flex
      position="absolute"
      top={align === 'center' ? '50%' : undefined}
      bottom={align === 'bottom' ? 0 : undefined}
      align="center"
      justify="center"
      direction="column"
      gap={12}
      w="100%"
      h={align === 'center' ? '100%' : undefined}
      p="32px 0"
      bg={align === 'center' ? 'rgba(0, 0, 0, 0.60)' : BACKGROUND_GRADIENT}
      css={{
        whiteSpace: 'pre-line',
        transform: align === 'bottom' ? 'none' : 'translateY(-50%)',
        borderRadius: 'inherit',
      }}
    >
      {children}
    </Flex>
  );
}

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
`;

const Video = styled.video`
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;

  /* GPU 가속 최적화 */
  transform: translateZ(0);
  will-change: transform, opacity;
  backface-visibility: hidden;
`;

const Blur = styled.div`
  width: 100%;
  height: 100%;
  backdrop-filter: blur(30px);
`;

const BlurText = styled.p`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 56px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foregroundInverse};
  white-space: pre-line;
`;

const StyledButton = styled.button`
  display: flex;
  flex-direction: row;
  width: fit-content;
  padding: 10px 14px;
  gap: 4px;
  border-radius: 8%;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(35px);

  color: ${({ theme }) => theme.colors.foregroundInverse};
  ${FONT_STYLE.body14L20}
  font-weight: 600;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    ${FONT_STYLE.body16L24}
    padding: 8px 16px;
  `,
  )}
`;

const StyledCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.20);

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    width: 60px;
    height: 60px;
  `,
  )}
`;

const SlideBox = styled.div<{ isOpen: boolean }>`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0; /* inset: 0; 과 동일 */
  padding: 20px;
  flex-direction: column;
  justify-content: space-between;

  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(30px);
  border-radius: inherit;

  /* 애니메이션 대상 */
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};

  /* opacity는 0.4s로 페이드, visibility는 닫힐 때(=isOpen:false)만 0.4s 지연 후 hidden */
  transition: opacity 0.4s ease-out, visibility 0s linear ${({ isOpen }) => (isOpen ? '0s' : '0.4s')};

  /* 성능/합성 힌트 */
  will-change: opacity;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    padding: 30px;
  `,
  )}
`;

ContentsCard.displayName = 'ContentsCard';

// top: 50%;
//     transform: translateY(-50%);
